import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridActionsColDef,
  GridFilterInitialState,
  GridRowId,
  GridRowParams,
  GridSelectionModel,
  GridSortingInitialState,
  GridCallbackDetails,
  GridInitialState,
  useGridApiRef,
  ValueOptions,
  GridCellEditStopParams,
  GridCellEditStopReasons,
  MuiEvent,
} from '@mui/x-data-grid-premium';
import { defaultPageSize, defaultPageSizeOptions } from 'constants/datagrid';
import { Box, LinearProgress } from '@mui/material';
import { useVeltInitState } from '@veltdev/react';
import _ from 'lodash';
import {
  Icon as TablerIcon,
  IconColumns,
  IconDownload,
  IconFilter,
  IconMinus,
  IconPlus,
  IconThumbUp,
  IconThumbDown,
  IconThumbUpOff,
  IconThumbDownOff,
  IconArchive,
  IconArchiveOff,
  IconTrash,
  IconLockOpen,
  IconLock,
} from '@tabler/icons-react';
import {
  DatagridNames,
  ExtendedGridColDef,
  RowAction,
  DatagridRecordButtonProps,
  VisibleColumnGroup,
} from 'types/datagrid';
import {
  appPermissionAccessLevels,
  appPermissionStates,
  RecordType,
} from 'types/apps';
import {
  booleanFilterOperators,
  booleanValueOptions,
  getApplyFilterFnCaseInsensitive,
} from 'ui-component/DataGrid/FilterOperators';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import VerboseLoader, {
  VerboseLoaderProps,
} from 'ui-component/extended/VerboseLoader';
import { Theme, useTheme } from '@mui/material/styles';
import { COLOR_OPTIONS, FeatureFlags } from 'types';
import { SxProps } from '@mui/system';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { FieldValues } from 'react-hook-form';
import {
  BooleanIconRenderCell,
  renderMutationCellWithInput,
  RenderUserNameCell,
} from 'ui-component/DataGrid/Render';
import renderCellWithEditWrapper from 'ui-component/DataGrid/Render/RenderCellWithEditWrapper';
import { useDeviceContext } from 'hooks/useDeviceContext';
import ExtendedGridActionsCellItem from 'ui-component/DataGrid/ExtendedGridActionsCellItem';
import {
  gridClasses,
  GridFilterModel,
  GridLinkOperator,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridSortModel,
  GridValidRowModel,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import DataGridChartsContainer from 'ui-component/DataGrid/DataGridChartsContainer';
import NoRowsOverlayComponent from 'ui-component/DataGrid/NoRowsOverlayComponent';
import ExtendedGridToolbar from 'ui-component/DataGrid/ExtendedGridToolbar';
import { ALL_APP_IDS } from 'constants/appConstants';
import { ALL_RECORD_TYPE_IDS, ALL_RECORD_TYPES } from 'constants/recordTypes';
import { GenericRecord, GenericResource } from 'types/api';
import { pluralizeWordOption } from 'utils/functions';
import { useDialogManager } from 'hooks/useDialogManager';
import DeleteRecordDialog from 'ui-component/clientV2/DeleteRecordDialog';
import DeleteRecordsDialog from 'ui-component/clientV2/DeleteRecordsDialog';
import ToggleRecordsDialog from 'ui-component/clientV2/ToggleRecordsDialog';
import ToggleRecordDialog, {
  ToggleRecordActionTypes,
} from 'ui-component/clientV2/ToggleRecordDialog';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import useGetCustomPropertyColumns from 'hooks/useGetCustomPropertyColumns';
import {
  editableTagsColumnDef,
  tagsColumnDef,
} from 'ui-component/DataGrid/tagsColumnDef';
import { EditInlineTeamAutocomplete } from 'ui-component/HookFormComponents/InlineEditInputComponents';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import ExternallySyncedInfoSection from 'ui-component/ExternallySyncedInfoSection';
import {
  dateValueFormatter,
  userNameValueFormatter,
} from 'ui-component/DataGrid/ValueFormatters';
import { UserInfoSize } from 'ui-component/UserInfoSection';
import VeltGridActionsCellItem from 'ui-component/Velt/VeltGridActionsCellItem';
import { Org } from 'types/org';
import { useUpdateOrgData } from 'hooks/useUpdateOrgData';
import InlineGoToButton from 'ui-component/shared/InlineGoToButton';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { useSelector } from 'store';
import { userNameComparator } from 'ui-component/DataGrid/SortComparators';
import { formatToDateTime } from 'utils/dates';
import { dateValueGetter } from 'ui-component/DataGrid/ValueGetters';
import { useFeature } from '@growthbook/growthbook-react';
import useSnackbar from 'hooks/useSnackbar';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import useAddDatagridBreadcrumb, {
  DATAGRID_BREADCRUMB_TYPES,
} from 'hooks/useAddDatagridBreadcrumb';

enum RECORD_ACTION_DIALOGS {
  ARCHIVE = 'ARCHIVE',
  DELETE = 'DELETE',
  APPROVE = 'APPROVE',
  VOID = 'VOID',
  LOCK = 'LOCK',
  BATCH_ARCHIVE = 'BATCH_ARCHIVE',
  BATCH_DELETE = 'BATCH_DELETE',
  BATCH_APPROVE = 'BATCH_APPROVE',
  BATCH_VOID = 'BATCH_VOID',
  BATCH_LOCK = 'BATCH_LOCK',
}

type DynamicFilter = {
  field: string;
  options: ValueOptions[];
};

type QueryParamState = {
  filter?: {
    filterModel: GridFilterModel;
  };
  sorting?: {
    sortModel: GridSortModel;
  };
  pagination?: {
    pageSize?: number;
    page?: number;
  };
};

export interface ExtendedDatagridProps<
  T extends GenericResource & GridValidRowModel,
  ReportDataType = never
> extends DataGridPremiumProps {
  children?: ReactNode;
  editable?: boolean;
  editLocked?: boolean;
  toolbarExport?: boolean;
  toolbarColumns?: boolean;
  toolbarFilters?: boolean;
  toolbarQuickFilter?: boolean;
  toolbarReset?: boolean;
  exportFileNamePrefix?: string | null;
  noRows?: string | ReactNode;
  initialSorting?: GridSortingInitialState | null;
  initialFilter?: GridFilterInitialState | null;
  initialVisibleColumns?: string[] | null;
  visibleColumnGroups?: VisibleColumnGroup[] | null;
  initialPinned?: string[] | null;
  pinnedActions?: boolean;
  gridName: DatagridNames;
  detailPanel?: boolean;
  detailExclusiveOpen?: boolean;
  hideToolbar?: boolean;
  noRowHeightAuto?: boolean;
  initialSx?: SxProps<Theme>;
  dynamicFilters?: DynamicFilter[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  columns: ExtendedGridColDef<T>[] | ExtendedGridColDef[];
  exportCsv?: () => void;
  isExportLoading?: boolean;
  apiRefServerSide?: MutableRefObject<GridApiPremium>;
  verboseLoader?: VerboseLoaderProps;
  generateReportData?: ReportDataType;
  pageSizeOptions?: number[];
  showCharts?: boolean;
  loadingCharts?: boolean;
  overridePageSize?: number;
  shouldSaveDatagridState?: boolean;
  newRecordButton?: DatagridRecordButtonProps;
  uploadRecordButton?: DatagridRecordButtonProps;
  rowActions?: RowAction<T>[];
  fullCard?: boolean;
  recordType?: ALL_RECORD_TYPE_IDS | null;
  hideCommenting?: boolean;
  forceEnableCommenting?: boolean;
  enableStateQueryParams?: boolean;
  getRowClickPath?: (row: T) => string;
  getRowClickOrg?: (row: T) => Org['id'];
}

const defaultInitialSx = {};
const defaultOnPageSizeChange = () => {};
const defaultOnPageChange = () => {};

const ExtendedDatagrid = <T extends GenericResource, ReportDataType = never>({
  children,
  loading = false,
  pagination = true,
  editable = false,
  editLocked = false,
  toolbarExport = true,
  toolbarColumns = true,
  toolbarFilters = true,
  toolbarQuickFilter = true,
  toolbarReset = true,
  exportFileNamePrefix = null,
  noRows = 'No records found',
  initialSorting = null,
  initialFilter = null,
  initialVisibleColumns = null,
  visibleColumnGroups = null,
  initialPinned = null,
  pinnedActions = true,
  gridName,
  detailPanel = false,
  detailExclusiveOpen = false,
  getDetailPanelContent,
  onRowClick,
  checkboxSelection = false,
  columns,
  rows,
  hideToolbar = false,
  noRowHeightAuto = false,
  initialSx = defaultInitialSx,
  dynamicFilters,
  onPageSizeChange = defaultOnPageSizeChange,
  onPageChange = defaultOnPageChange,
  exportCsv,
  isExportLoading,
  apiRefServerSide,
  verboseLoader,
  generateReportData,
  pageSizeOptions = defaultPageSizeOptions,
  showCharts = false,
  loadingCharts = false,
  overridePageSize,
  shouldSaveDatagridState = true,
  newRecordButton = {
    label: 'Add',
    disabled: false,
    onClick: undefined,
  },
  uploadRecordButton = {
    label: 'Upload',
    disabled: false,
    onClick: undefined,
  },
  aggregationModel,
  rowActions = [],
  onSelectionModelChange,
  fullCard = false,
  recordType,
  getRowId,
  hideCommenting = false,
  forceEnableCommenting,
  enableStateQueryParams = false,
  getRowClickPath,
  getRowClickOrg,
  ...others
}: ExtendedDatagridProps<T, ReportDataType>) => {
  const tempVelt = useFeature(FeatureFlags.tempVelt).on;
  const isVeltInitiated = useVeltInitState();

  const { changeActiveOrg } = useUpdateOrgData();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatchErrorSnackbar } = useSnackbar();
  const { narrowWindow } = useDeviceContext();
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();
  const { viewAsStaff } = useSelector((state) => state.user);
  const { data: currentUser } = useGetUserQuery(undefined);
  const { activeOrgId, blended } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const gridState = useSelector((state) => state.datagrid[gridName]);
  // Use direct reference to Redux state instead of useState to ensure column ordering
  // updates immediately reflect when saved, preventing the old cached state from
  // overriding user's column order changes during re-renders
  const savedDatagridState = gridState;

  const [hasSavedInitialState, setHasSavedInitialState] = useState(false);
  const { handleLogSavedState } = useAddDatagridBreadcrumb();

  useEffect(() => {
    if (savedDatagridState && !hasSavedInitialState) {
      handleLogSavedState({
        gridState: savedDatagridState,
        gridName,
        type: DATAGRID_BREADCRUMB_TYPES.INITIAL_LOADED,
      });
      setHasSavedInitialState(true);
    }
  }, [savedDatagridState, gridName, hasSavedInitialState, handleLogSavedState]);

  const {
    appPermissions,
    appsWithPermissions,
    isLoading: isLoadingAppAccess,
    isPermissionDisabled,
  } = useAppAccessContext();
  const apiRefClientSide = useGridApiRef();
  const apiRef = apiRefServerSide || apiRefClientSide;

  const [apiRefReady, setApiRefReady] = useState(false);

  const { openDialog, closeDialog, isDialogOpen } = useDialogManager(
    Object.values(RECORD_ACTION_DIALOGS)
  );

  useEffect(() => {
    if (apiRef?.current && !apiRefReady) {
      setApiRefReady(true);
    }
  }, [apiRef, apiRefReady]);

  // Parse query parameters
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const getQueryParamState = useCallback(() => {
    if (!enableStateQueryParams) return {};

    const state: QueryParamState = {};

    // Parse filter
    const filterParam = queryParams.get('filter');
    if (filterParam) {
      try {
        const parsedFilter = JSON.parse(filterParam);
        state.filter = {
          filterModel: {
            items: parsedFilter,
            linkOperator: GridLinkOperator.And,
          },
        };
      } catch (e) {
        dispatchErrorSnackbar(`Invalid filter query param: ${filterParam}`);
      }
    }

    // Parse quick filter
    const quickFilterParam = queryParams.get('quickFilter');
    if (quickFilterParam) {
      if (!state.filter) {
        state.filter = {
          filterModel: {
            items: [],
            linkOperator: GridLinkOperator.And,
          },
        };
      }
      state.filter.filterModel.quickFilterValues = quickFilterParam.split(',');
    }

    // Parse sort
    const sortParam = queryParams.get('sort');
    if (sortParam) {
      try {
        state.sorting = { sortModel: JSON.parse(sortParam) };
      } catch (e) {
        dispatchErrorSnackbar(`Invalid sort query param: ${sortParam}`);
      }
    }

    // Parse pagination
    const pageSizeParam = queryParams.get('pageSize');
    const pageParam = queryParams.get('page');
    if (pageSizeParam || pageParam) {
      state.pagination = {
        ...(pageSizeParam && { pageSize: parseInt(pageSizeParam, 10) }),
        ...(pageParam && { page: parseInt(pageParam, 10) }),
      };
    }

    return state;
  }, [queryParams, enableStateQueryParams]);

  const queryParamState = getQueryParamState();

  const [pageSize, setPageSize] = useState<number>(
    overridePageSize ??
      queryParamState.pagination?.pageSize ??
      savedDatagridState?.pagination?.pageSize ??
      defaultPageSize
  );

  const [page, setPage] = useState<number>(
    queryParamState.pagination?.page ?? 0
  );

  useEffect(() => onPageSizeChange(pageSize), []);
  useEffect(() => onPageChange(page), []);

  // Update query parameters when state changes
  const updateQueryParams = useCallback(
    (updates: Record<string, string | null>) => {
      if (!enableStateQueryParams) return;

      const newParams = new URLSearchParams(location.search);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });

      const newSearch = newParams.toString();
      const currentSearch = location.search.substring(1); // Remove the leading '?'

      if (newSearch !== currentSearch) {
        navigate(
          {
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : '',
          },
          { replace: true }
        );
      }
    },
    [enableStateQueryParams, location, navigate]
  );

  const recordTypeDefinition = recordType
    ? (ALL_RECORD_TYPES[recordType] as unknown as RecordType<T>)
    : null;

  const {
    customPropertyColumns,
    isFetching: isFetchingCustomProperties,
    hasFetched: hasFetchedCustomProperties,
  } = useGetCustomPropertyColumns({
    model: recordTypeDefinition?.customPropertyModel || CustomPropertyModel.ORG,
    visibilityContext: editable
      ? CustomPropertyVisibility.DATAGRID_EDIT
      : CustomPropertyVisibility.DATAGRID_READ_ONLY,
    useMutation: recordTypeDefinition?.updateMutation,
    mutationAsClientV2: true,
  });

  const editPermissionScope = recordTypeDefinition?.app
    ? {
        app: recordTypeDefinition?.app,
        accessLevel: appPermissionAccessLevels.edit,
      }
    : undefined;

  // Prevent losing MUI column state by caching columns. Column order
  // is in MUI DataGridPremimum local state and it's the only thing
  // that is uncontrolled (compared to column visibility, filters, etc.)
  const cachedColumnsRef = useRef<ExtendedGridColDef[]>(
    columns as ExtendedGridColDef[]
  );
  useEffect(() => {
    if (!_.isEqual(cachedColumnsRef.current, columns)) {
      cachedColumnsRef.current = columns as ExtendedGridColDef[];
    }
  }, [columns]);

  // Helper function to apply column ordering from persisted state
  const applyColumnOrdering = useCallback(
    (columnsToOrder: ExtendedGridColDef[]): ExtendedGridColDef[] => {
      const savedOrder = savedDatagridState?.columns?.orderedFields;
      if (!savedOrder || savedOrder.length === 0) {
        return columnsToOrder;
      }

      // Create a map of field to column for quick lookup
      const columnMap = new Map<string, ExtendedGridColDef>();
      columnsToOrder.forEach((col) => {
        columnMap.set(col.field, col);
      });

      // Apply the saved order, keeping only columns that exist
      const orderedColumns: ExtendedGridColDef[] = [];
      const usedFields = new Set<string>();

      // First, add columns in the saved order
      savedOrder.forEach((field) => {
        const column = columnMap.get(field);
        if (column) {
          orderedColumns.push(column);
          usedFields.add(field);
        }
      });

      // Then, add any remaining columns that weren't in the saved order
      columnsToOrder.forEach((col) => {
        if (!usedFields.has(col.field)) {
          orderedColumns.push(col);
        }
      });

      return orderedColumns;
    },
    [savedDatagridState?.columns?.orderedFields]
  );

  const combinedColumns = useMemo(
    () => [
      ...(recordTypeDefinition?.externallySyncableRecord
        ? [
            {
              field: 'dataSourceSyncedAt|externallySynced|dataSource',
              headerName: 'Integration Source',
              width: 150,
              type: 'string',
              renderCell: (params: GridRenderCellParams) => (
                <ExternallySyncedInfoSection
                  record={params.row}
                  size={UserInfoSize.SM}
                />
              ),
              allowInBlended: true,
            } as ExtendedGridColDef,
          ]
        : []),
      ...cachedColumnsRef.current,
      ...(recordTypeDefinition?.customPropertyModel
        ? customPropertyColumns
        : []),
      ...(recordTypeDefinition?.tagRecord &&
      (!editable || !recordTypeDefinition?.updateMutation)
        ? [tagsColumnDef(recordTypeDefinition?.singularName)]
        : []),
      ...(recordTypeDefinition?.tagRecord &&
      editable &&
      recordTypeDefinition?.updateMutation
        ? [
            editableTagsColumnDef(
              recordTypeDefinition?.singularName,
              recordTypeDefinition?.updateMutation,
              recordTypeDefinition?.flagshipModel
            ),
          ]
        : []),
      ...(recordTypeDefinition?.teamRecord &&
      (!editable || !recordTypeDefinition?.updateMutation)
        ? [
            {
              field: 'team',
              headerName: handleReplaceWithCustomTeamName('Team'),
              description: handleReplaceWithCustomTeamName(
                `Team associated with this ${recordTypeDefinition?.singularName}`
              ),
              permissionScope: { app: ALL_APP_IDS.PROPERTIES },
              valueGetter: (params: GridValueGetterParams) =>
                params.row.team?.name || '-',
            } as ExtendedGridColDef,
          ]
        : []),
      ...(recordTypeDefinition?.teamRecord &&
      editable &&
      recordTypeDefinition?.updateMutation
        ? [
            {
              field: 'team',
              headerName: handleReplaceWithCustomTeamName('Team'),
              description: handleReplaceWithCustomTeamName(
                `Team associated with this ${recordTypeDefinition?.singularName}`
              ),
              type: 'string',
              flex: 1,
              editable: true,
              inlineEditRenderCell: true,
              useMutation: recordTypeDefinition?.updateMutation,
              mutationInputElement: EditInlineTeamAutocomplete,
              getMutationDefaultValue: (params: GridRenderEditCellParams) =>
                params.row.team?.id || '',
              valueGetter: (params: GridValueGetterParams) =>
                params.row.team?.name || '-',
              permissionScope: {
                app: ALL_APP_IDS.PROPERTIES,
              },
              mutationAsClientV2: true,
            } as ExtendedGridColDef,
          ]
        : []),
      ...(recordTypeDefinition?.approveMutation
        ? ([
            {
              field: 'approvedAt',
              headerName: 'Approval Date',
              description: `Date this ${recordTypeDefinition?.singularName} was approved`,
              type: 'date',
              valueFormatter: dateValueFormatter,
              valueGetter: dateValueGetter,
              flex: 0.5,
            },
            {
              field: 'approvedBy|approvedAt',
              headerName: 'Approved',
              description: `User that approved this ${recordTypeDefinition?.singularName}`,
              type: 'string',
              renderCell: (params: GridRenderCellParams<string>) =>
                params.row.approvedAt ? (
                  RenderUserNameCell(
                    params.row.approvedBy,
                    formatToDateTime(params.row.approvedAt),
                    true
                  )
                ) : (
                  <></>
                ),
              valueGetter: (params: GridValueGetterParams) =>
                params.row.approvedBy,
              valueFormatter: (params: GridValueFormatterParams) =>
                userNameValueFormatter({
                  user: params.value,
                  currentUser,
                  viewAsStaff: viewAsStaff || false,
                }),
              sortComparator: userNameComparator,
            },
            {
              field: 'approved',
              headerName: 'Approved',
              description: `Indicates whether this ${recordTypeDefinition?.singularName} is approved`,
              type: 'boolean',
              flex: 0.3,
              hideable: true,
              filterOperators: booleanFilterOperators,
              valueOptions: booleanValueOptions,
              valueGetter: (params: GridValueGetterParams) =>
                !!params.row.approvedAt,
              renderCell: (params: GridRenderCellParams) =>
                BooleanIconRenderCell(
                  params,
                  IconThumbUp,
                  IconThumbUpOff,
                  'success',
                  'error'
                ),
            },
          ] as ExtendedGridColDef[])
        : []),
      ...(recordTypeDefinition?.lockMutation
        ? ([
            {
              field: 'lockedAt',
              headerName: 'Lock Date',
              description: `Date this ${recordTypeDefinition?.singularName} was locked`,
              type: 'date',
              valueFormatter: dateValueFormatter,
              valueGetter: dateValueGetter,
              flex: 0.5,
            },
            {
              field: 'lockedBy|lockedAt',
              headerName: 'Locked',
              description: `User that locked this ${recordTypeDefinition?.singularName}`,
              type: 'string',
              renderCell: (params: GridRenderCellParams<string>) =>
                params.row.lockedAt ? (
                  RenderUserNameCell(
                    params.row.lockedBy,
                    formatToDateTime(params.row.lockedAt),
                    true
                  )
                ) : (
                  <></>
                ),
              valueGetter: (params: GridValueGetterParams) =>
                params.row.approvedBy,
              valueFormatter: (params: GridValueFormatterParams) =>
                userNameValueFormatter({
                  user: params.value,
                  currentUser,
                  viewAsStaff: viewAsStaff || false,
                }),
              sortComparator: userNameComparator,
            },
            {
              field: 'locked',
              headerName: 'Locked',
              description: `Indicates whether this ${recordTypeDefinition?.singularName} is locked`,
              type: 'boolean',
              flex: 0.3,
              hideable: true,
              filterOperators: booleanFilterOperators,
              valueOptions: booleanValueOptions,
              valueGetter: (params: GridValueGetterParams) =>
                !!params.row.lockedAt,
              renderCell: (params: GridRenderCellParams) =>
                BooleanIconRenderCell(
                  params,
                  IconLock,
                  IconLockOpen,
                  'error',
                  'primary'
                ),
            },
          ] as ExtendedGridColDef[])
        : []),
      ...(recordTypeDefinition?.voidMutation
        ? ([
            {
              field: 'voidedAt',
              headerName: 'Rejection Date',
              description: `Date this ${recordTypeDefinition?.singularName} was rejected`,
              type: 'date',
              valueFormatter: dateValueFormatter,
              valueGetter: dateValueGetter,
              flex: 0.5,
            },
            {
              field: 'voidedBy|voidedAt',
              headerName: 'Rejected',
              description: `User that rejected this ${recordTypeDefinition?.singularName}`,
              type: 'string',
              renderCell: (params: GridRenderCellParams<string>) =>
                params.row.voidedAt ? (
                  RenderUserNameCell(
                    params.row.voidedBy,
                    formatToDateTime(params.row.voidedAt),
                    true
                  )
                ) : (
                  <></>
                ),
              valueGetter: (params: GridValueGetterParams) =>
                params.row.voidedBy,
              valueFormatter: (params: GridValueFormatterParams) =>
                userNameValueFormatter({
                  user: params.value,
                  currentUser,
                  viewAsStaff: viewAsStaff || false,
                }),
              sortComparator: userNameComparator,
            },
          ] as ExtendedGridColDef[])
        : []),
      ...(recordTypeDefinition?.archiveMutation
        ? ([
            {
              field: 'archivedAt',
              headerName: 'Archived Date',
              description: `Date this ${recordTypeDefinition?.singularName} was archived`,
              type: 'date',
              valueFormatter: dateValueFormatter,
              valueGetter: dateValueGetter,
              flex: 0.5,
            },
            {
              field: 'archivedBy|archivedAt',
              headerName: 'Archived By',
              description: `User that archived this ${recordTypeDefinition?.singularName}`,
              type: 'string',
              renderCell: (params: GridRenderCellParams<string>) =>
                params.row.archivedAt ? (
                  RenderUserNameCell(
                    params.row.archivedBy,
                    formatToDateTime(params.row.archivedAt),
                    true
                  )
                ) : (
                  <></>
                ),
              valueGetter: (params: GridValueGetterParams) =>
                params.row.archivedBy,
              valueFormatter: (params: GridValueFormatterParams) =>
                userNameValueFormatter({
                  user: params.value,
                  currentUser,
                  viewAsStaff: viewAsStaff || false,
                }),
              sortComparator: userNameComparator,
            },
            {
              field: 'archived',
              headerName: 'Archived',
              description: `Indicates whether this ${recordTypeDefinition?.singularName} is archived`,
              type: 'boolean',
              flex: 0.3,
              hideable: true,
              filterOperators: booleanFilterOperators,
              valueOptions: booleanValueOptions,
              renderCell: (params: GridRenderCellParams) =>
                BooleanIconRenderCell(
                  params,
                  IconArchive,
                  IconArchiveOff,
                  'error',
                  'primary'
                ),
            },
          ] as ExtendedGridColDef[])
        : []),
    ],
    [
      cachedColumnsRef.current,
      customPropertyColumns,
      recordTypeDefinition,
      editable,
      currentUser,
      viewAsStaff,
    ]
  );

  // handle page size change
  const handlePageSizeChangeCallback = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      onPageSizeChange(newPageSize);
      if (enableStateQueryParams) {
        updateQueryParams({
          pageSize:
            newPageSize !== defaultPageSize ? newPageSize.toString() : null,
        });
      }
    },
    [onPageSizeChange, enableStateQueryParams, updateQueryParams]
  );

  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    GridRowId[]
  >([]);
  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (ids: GridRowId[]) => {
      setDetailPanelExpandedRowIds(ids);
    },
    []
  );

  // create row actions based on recordTypeDefinition
  const combinedRowActions = useMemo(
    () => [
      ...(recordTypeDefinition?.approveMutation
        ? [
            {
              onRowClick: (row: T) => {
                if (row) {
                  setSelectedRow(row);
                  openDialog(RECORD_ACTION_DIALOGS.APPROVE);
                }
              },
              onBatchClick: () => {
                openDialog(RECORD_ACTION_DIALOGS.BATCH_APPROVE);
              },
              getLabel: (row?: T) =>
                row?.approvedAt ? `Unapprove` : `Approve`,
              getBatchLabel: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return `Approve`;
                }
                if ((selectedRows || []).every((row) => row.approvedAt)) {
                  return `Unapprove`;
                }
                return `Approve`;
              },
              getRowIcon: (row?: T) =>
                row?.approvedAt ? IconThumbUpOff : IconThumbUp,
              getBatchIcon: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return IconThumbUp;
                }
                if ((selectedRows || []).every((row) => row.approvedAt)) {
                  return IconThumbUpOff;
                }
                return IconThumbUp;
              },
              showInRowMenu: true,
              permissionScope: {
                app: recordTypeDefinition?.app,
                accessLevel: appPermissionAccessLevels.edit,
              },
              color: 'success',
              getBatchTooltip: (selectedRowCount) =>
                `Approve ${selectedRowCount.toLocaleString(
                  'en-US'
                )} ${pluralizeWordOption(
                  recordTypeDefinition?.singularName,
                  recordTypeDefinition?.pluralName,
                  selectedRowCount
                )}`,
              getRowRequirements: (row?: T & GenericRecord) => [
                {
                  disabled: !!row?.externallySynced,
                  disabledReason: `This ${recordTypeDefinition?.singularName} is controlled by an external system integration`,
                },
                {
                  disabled:
                    recordTypeDefinition.requireAdminApprovals &&
                    org?.requireAdminApprovals &&
                    !isPermissionDisabled({
                      app: recordTypeDefinition.app,
                      accessLevel: appPermissionAccessLevels.admin,
                    }),
                  disabledReason: `Only admins can approve ${recordTypeDefinition?.pluralName}`,
                },
              ],
              getBatchRequirements: (selectedRows?: (T & GenericRecord)[]) => [
                {
                  disabled: (selectedRows || []).some(
                    (row) => row.externallySynced
                  ),
                  disabledReason: `${
                    selectedRows?.length
                  } ${pluralizeWordOption(
                    recordTypeDefinition?.singularName,
                    recordTypeDefinition?.pluralName,
                    (selectedRows || []).length
                  )} ${pluralizeWordOption(
                    'is',
                    'are',
                    (selectedRows || []).length
                  )} controlled by an external system integration`,
                },
                {
                  disabled:
                    recordTypeDefinition.requireAdminApprovals &&
                    org?.requireAdminApprovals &&
                    !isPermissionDisabled({
                      app: recordTypeDefinition.app,
                      accessLevel: appPermissionAccessLevels.admin,
                    }),
                  disabledReason: `Only admins can approve ${recordTypeDefinition?.pluralName}`,
                },
              ],
            } as RowAction<T & GenericRecord>,
          ]
        : []),
      ...(recordTypeDefinition?.voidMutation
        ? [
            {
              onRowClick: (row: T & GenericRecord) => {
                if (row) {
                  setSelectedRow(row);
                  openDialog(RECORD_ACTION_DIALOGS.VOID);
                }
              },
              onBatchClick: () => {
                openDialog(RECORD_ACTION_DIALOGS.BATCH_VOID);
              },
              getLabel: (row?: T) => (row?.voidedAt ? `Unreject` : `Reject`),
              getBatchLabel: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return `Reject`;
                }
                if ((selectedRows || []).every((row) => row.voidedAt)) {
                  return `Unreject`;
                }
                return `Reject`;
              },
              getRowIcon: (row?: T) =>
                row?.voidedAt ? IconThumbDownOff : IconThumbDown,
              getBatchIcon: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return IconThumbDown;
                }
                if ((selectedRows || []).every((row) => row.voidedAt)) {
                  return IconThumbDownOff;
                }
                return IconThumbDown;
              },
              showInRowMenu: true,
              permissionScope: {
                app: recordTypeDefinition?.app,
                accessLevel: appPermissionAccessLevels.edit,
              },
              color: 'error',
              getBatchTooltip: (selectedRowCount) =>
                `Reject ${selectedRowCount.toLocaleString(
                  'en-US'
                )} ${pluralizeWordOption(
                  recordTypeDefinition?.singularName,
                  recordTypeDefinition?.pluralName,
                  selectedRowCount
                )}`,
              getRowRequirements: (row?: T & GenericRecord) => [
                {
                  disabled: !!row?.externallySynced,
                  disabledReason: `This ${recordTypeDefinition?.singularName} is controlled by an external system integration`,
                },
              ],
              getBatchRequirements: (selectedRows?: (T & GenericRecord)[]) => [
                {
                  disabled: (selectedRows || []).some(
                    (row) => row.externallySynced
                  ),
                  disabledReason: `${
                    selectedRows?.length
                  } ${pluralizeWordOption(
                    recordTypeDefinition?.singularName,
                    recordTypeDefinition?.pluralName,
                    (selectedRows || []).length
                  )} ${pluralizeWordOption(
                    'is',
                    'are',
                    (selectedRows || []).length
                  )} controlled by an external system integration`,
                },
              ],
            } as RowAction<T & GenericRecord>,
          ]
        : []),
      ...rowActions,
      ...(recordTypeDefinition?.lockMutation
        ? [
            {
              onRowClick: (row: T) => {
                if (row) {
                  setSelectedRow(row);
                  openDialog(RECORD_ACTION_DIALOGS.LOCK);
                }
              },
              onBatchClick: () => {
                openDialog(RECORD_ACTION_DIALOGS.BATCH_LOCK);
              },
              getLabel: (row?: T) => (row?.lockedAt ? `Unlock` : `Lock`),
              getBatchLabel: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return `Lock`;
                }
                if ((selectedRows || []).every((row) => row.lockedAt)) {
                  return `Unlock`;
                }
                return `Lock`;
              },
              getRowIcon: (row?: T) =>
                row?.lockedAt ? IconLockOpen : IconLock,
              getBatchIcon: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return IconLock;
                }
                if ((selectedRows || []).every((row) => row.lockedAt)) {
                  return IconLockOpen;
                }
                return IconLock;
              },
              showInRowMenu: true,
              permissionScope: {
                app: recordTypeDefinition?.app,
                accessLevel: appPermissionAccessLevels.edit,
              },
              color: 'primary',
              getBatchTooltip: (selectedRowCount) =>
                `Lock ${selectedRowCount.toLocaleString(
                  'en-US'
                )} ${pluralizeWordOption(
                  recordTypeDefinition?.singularName,
                  recordTypeDefinition?.pluralName,
                  selectedRowCount
                )}`,
              getRowRequirements: (row?: T & GenericRecord) => [
                {
                  disabled: !!row?.externallySynced,
                  disabledReason: `This ${recordTypeDefinition?.singularName} is controlled by an external system integration`,
                },
                {
                  disabled:
                    recordTypeDefinition.requireAdminApprovals &&
                    org?.requireAdminApprovals &&
                    !isPermissionDisabled({
                      app: recordTypeDefinition.app,
                      accessLevel: appPermissionAccessLevels.admin,
                    }),
                  disabledReason: `Only admins can lock ${recordTypeDefinition?.pluralName}`,
                },
              ],
              getBatchRequirements: (selectedRows?: (T & GenericRecord)[]) => [
                {
                  disabled: (selectedRows || []).some(
                    (row) => row.externallySynced
                  ),
                  disabledReason: `${
                    selectedRows?.length
                  } ${pluralizeWordOption(
                    recordTypeDefinition?.singularName,
                    recordTypeDefinition?.pluralName,
                    (selectedRows || []).length
                  )} ${pluralizeWordOption(
                    'is',
                    'are',
                    (selectedRows || []).length
                  )} controlled by an external system integration`,
                },
                {
                  disabled:
                    recordTypeDefinition.requireAdminApprovals &&
                    org?.requireAdminApprovals &&
                    !isPermissionDisabled({
                      app: recordTypeDefinition.app,
                      accessLevel: appPermissionAccessLevels.admin,
                    }),
                  disabledReason: `Only admins can lock ${recordTypeDefinition?.pluralName}`,
                },
              ],
            } as RowAction<T & GenericRecord>,
          ]
        : []),
      ...(recordTypeDefinition?.archiveMutation
        ? [
            {
              onRowClick: (row: T & GenericRecord) => {
                if (row) {
                  setSelectedRow(row);
                  openDialog(RECORD_ACTION_DIALOGS.ARCHIVE);
                }
              },
              onBatchClick: () => {
                openDialog(RECORD_ACTION_DIALOGS.BATCH_ARCHIVE);
              },
              getLabel: (row?: T) =>
                row?.archivedAt ? `Unarchive` : `Archive`,
              getBatchLabel: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return `Archive`;
                }
                if ((selectedRows || []).every((row) => row.archivedAt)) {
                  return `Unarchive`;
                }
                return `Archive`;
              },
              getRowIcon: (row?: T) =>
                row?.archivedAt ? IconArchiveOff : IconArchive,
              getBatchIcon: (selectedRows?: (T & GenericRecord)[]) => {
                if (!selectedRows?.length) {
                  return IconArchive;
                }
                if ((selectedRows || []).every((row) => row.archivedAt)) {
                  return IconArchiveOff;
                }
                return IconArchive;
              },
              showInRowMenu: true,
              permissionScope: {
                app: recordTypeDefinition?.app,
                accessLevel: appPermissionAccessLevels.edit,
              },
              color: 'secondary',
              getBatchTooltip: (selectedRowCount) =>
                `Archive ${selectedRowCount.toLocaleString(
                  'en-US'
                )} ${pluralizeWordOption(
                  recordTypeDefinition?.singularName,
                  recordTypeDefinition?.pluralName,
                  selectedRowCount
                )}`,
              getRowRequirements: (row?: T & GenericRecord) => [
                {
                  disabled: !!row?.externallySynced,
                  disabledReason: `This ${recordTypeDefinition?.singularName} is controlled by an external system integration`,
                },
              ],
              getBatchRequirements: (selectedRows?: (T & GenericRecord)[]) => [
                {
                  disabled: (selectedRows || []).some(
                    (row) => row.externallySynced
                  ),
                  disabledReason: `${
                    selectedRows?.length
                  } ${pluralizeWordOption(
                    recordTypeDefinition?.singularName,
                    recordTypeDefinition?.pluralName,
                    (selectedRows || []).length
                  )} ${pluralizeWordOption(
                    'is',
                    'are',
                    (selectedRows || []).length
                  )} controlled by an external system integration`,
                },
              ],
            } as RowAction<T & GenericRecord>,
          ]
        : []),
      ...(recordTypeDefinition?.deleteMutation
        ? [
            {
              onRowClick: (row: T & GenericRecord) => {
                if (row) {
                  setSelectedRow(row);
                  openDialog(RECORD_ACTION_DIALOGS.DELETE);
                }
              },
              onBatchClick: () => {
                openDialog(RECORD_ACTION_DIALOGS.BATCH_DELETE);
              },
              getLabel: () => `Delete`,
              Icon: IconTrash,
              color: 'error',
              showInRowMenu: true,
              permissionScope: {
                app: recordTypeDefinition?.app,
                accessLevel: appPermissionAccessLevels.edit,
              },
              getBatchTooltip: (selectedRowCount) =>
                `Delete ${selectedRowCount.toLocaleString(
                  'en-US'
                )} ${pluralizeWordOption(
                  recordTypeDefinition?.singularName,
                  recordTypeDefinition?.pluralName,
                  selectedRowCount
                )}`,
            } as RowAction<T & GenericRecord>,
          ]
        : []),
    ],
    [rowActions, recordTypeDefinition]
  );

  // handle checkbox selection
  const calculatedCheckboxSelection =
    checkboxSelection ||
    _.filter(combinedRowActions, (action) => !!action.onBatchClick).length > 0;

  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);
  const selectedRowData = useMemo(
    () => rows.filter((row) => selectedRowIds.includes(row?.id?.toString())),
    [rows, selectedRowIds]
  );
  const handleSelect = (selectionModel: GridSelectionModel) => {
    setSelectedRowIds(selectionModel);
  };
  const [selectedRow, setSelectedRow] = useState<GenericRecord | null>(null);

  const enableVeltCommenting = useMemo(
    () =>
      isVeltInitiated &&
      tempVelt &&
      !hideCommenting &&
      !recordTypeDefinition?.childRecordTypes &&
      (recordTypeDefinition?.nameGetter || forceEnableCommenting),
    [
      isVeltInitiated,
      tempVelt,
      hideCommenting,
      recordTypeDefinition,
      forceEnableCommenting,
    ]
  );

  // generate action getter based on combinedRowActions prop
  const actionGetter = useCallback(
    ({ row }: GridRowParams) => [
      ...(enableVeltCommenting
        ? [
            <VeltGridActionsCellItem
              row={row}
              recordTypeDefinition={recordTypeDefinition}
              forceEnableCommenting={forceEnableCommenting}
            />,
          ]
        : []),
      ..._.chain(combinedRowActions)
        .filter((action) => !!action.onRowClick && !action.hideRowAction?.(row))
        .map((action) => {
          const ActionIcon: TablerIcon = action.getRowIcon
            ? action.getRowIcon(row)
            : action.Icon;

          const disabledRowRequirements = _.chain(
            action.getRowRequirements?.(row)
          )
            .filter((r) => r.disabled)
            .value();

          return (
            <ExtendedGridActionsCellItem
              onClick={() => {
                if (action.onRowClick) {
                  action.onRowClick(row);
                }
              }}
              color={action?.color ?? action.getColor?.(row)}
              icon={
                <ActionIcon
                  color={
                    action.disabled || action.isRowDisabled?.(row)
                      ? theme.palette.grey[500]
                      : _.get(theme.palette, [
                          action.color ||
                            action.getColor?.(row) ||
                            COLOR_OPTIONS.primary,
                          'main',
                        ])
                  }
                />
              }
              label={action.getLabel?.(row)}
              disabled={
                action.disabled ||
                action.isRowDisabled?.(row) ||
                disabledRowRequirements.length > 0
              }
              showInMenu={action.showInRowMenu}
              permissionScope={action.permissionScope}
              tooltip={
                disabledRowRequirements.length > 0
                  ? `${
                      action.getRowTooltip?.(row) || ''
                    }: ${disabledRowRequirements
                      .map((r) => r.disabledReason)
                      .join(', ')}`
                  : action.getRowTooltip?.(row)
              }
              tooltipPlacement={action.showInRowMenu ? 'left' : undefined}
            />
          );
        })
        .value(),
      ...(getRowClickPath
        ? [
            <InlineGoToButton
              link={getRowClickPath(row)}
              tooltip={`Go to ${
                recordTypeDefinition?.singularName || 'record'
              }`}
              recordName={recordTypeDefinition?.singularName || 'Record'}
            />,
          ]
        : []),
    ],
    [
      combinedRowActions,
      recordTypeDefinition,
      getRowClickPath,
      enableVeltCommenting,
      forceEnableCommenting,
      theme.palette,
    ]
  );

  // construct sx based on props
  const hasPartColumn = useMemo(
    () =>
      _.chain(combinedColumns)
        .map('headerName')
        .some((c) => _.includes(['Part', 'Part/Description'], c))
        .value(),
    [combinedColumns.length]
  );
  const sx: { [key: string]: object } = useMemo(() => {
    const sxObject: { [key: string]: object } = {
      '& ::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },
      '& ::-webkit-scrollbar-track': {
        backgroundColor: '#f5f5f5',
      },
      '& ::-webkit-scrollbar-thumb': {
        borderRadius: '10px',
        backgroundColor: '#c4c4c4',
      },
      '.MuiDataGrid-cell--withRenderer': {
        overflow: 'hidden',
        width: '100%',
      },
      [`.${gridClasses.cell}--editing:last-child`]: {
        display: 'flex',
        flexDirection: 'row-reverse',
      },
      '.MuiDataGrid-detailPanel': {
        backgroundColor: theme.palette.grey[100],
      },
      '.MuiDataGrid-row--detailPanelExpanded': {
        backgroundColor: theme.palette.grey[100],
      },
      '& .MuiDataGrid-pinnedRows': {
        boxShadow: 'none',
      },
      '& .MuiDataGrid-cell': {
        px: '3px',
      },
      '& .MuiDataGrid-footerContainer': {
        justifyContent: 'start',
      },
      ...(fullCard && {
        '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type':
          {
            paddingLeft: 2,
          },
      }),
    };

    if (!hasPartColumn) {
      sxObject['&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell'] = {
        py: '8px',
      };
      sxObject['&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell'] = {
        py: '15px',
      };
      sxObject['&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell'] = {
        py: '22px',
      };
    }
    if (onRowClick || getRowClickPath || editable) {
      sxObject['&:hover'] = {
        '.MuiDataGrid-row': {
          cursor: 'pointer',
        },
      };
    } else {
      sxObject['.MuiDataGrid-row.Mui-hovered'] = {
        background: 'white',
      };
      sxObject['& .MuiDataGrid-row:hover'] = {
        background: 'white',
      };
    }
    return sxObject;
  }, [
    hasPartColumn,
    !!onRowClick,
    editable,
    fullCard,
    !!getRowClickPath,
    theme.palette.grey,
  ]);

  // construct visible column groups
  const memoizedVisibleGroups = useMemo(() => {
    if (!visibleColumnGroups) {
      return null;
    }
    const groups = visibleColumnGroups;
    // add action columns to visibleColumnGroups
    groups.forEach((g) => {
      g.visibleColumns.push('actions');
    });

    // filter visibleColumnGroups based on appPermissions
    return groups.filter(
      (g) =>
        !g?.permissionScope?.app ||
        _.get(
          appPermissions,
          [g?.permissionScope.app, 'state'],
          appPermissionStates.disabled
        ) === appPermissionStates.enabled
    );
  }, [appPermissions, visibleColumnGroups]);

  // construct memoized columnVisibilityModel
  const memoizedColumnVisibilityModel: { [key: string]: boolean } =
    useMemo(() => {
      const columnVisibilityModel: { [key: string]: boolean } = {};

      const visibleColumns =
        initialVisibleColumns ?? _.map(combinedColumns, 'field');

      if (visibleColumns) {
        visibleColumns.push('actions');
        _.map(combinedColumns, 'field').forEach((col) => {
          columnVisibilityModel[col] = visibleColumns.includes(col);
        });
      }

      return columnVisibilityModel;
    }, [initialVisibleColumns, combinedColumns]);

  // construct combinedInitialState
  const combinedInitialState = useMemo<GridInitialState>(() => {
    const initialStateObject: { [key: string]: object } = {};

    // Apply initial state from props, then override with query params if enabled
    if (initialSorting) {
      initialStateObject.sorting = initialSorting;
    }
    if (queryParamState.sorting) {
      initialStateObject.sorting = queryParamState.sorting;
    }

    if (initialFilter) {
      initialStateObject.filter = initialFilter;
    }
    if (queryParamState.filter) {
      initialStateObject.filter = queryParamState.filter;
    }

    // Ensure filter has proper structure if it exists
    if (
      initialStateObject.filter &&
      !(initialStateObject.filter as any).filterModel
    ) {
      initialStateObject.filter = {
        filterModel: {
          items: [],
          linkOperator: 'and',
        },
      };
    }

    if (queryParamState.pagination) {
      initialStateObject.pagination = {
        pageSize: queryParamState.pagination.pageSize ?? defaultPageSize,
        page: queryParamState.pagination.page ?? 0,
      };
    }

    if (pinnedActions && !initialPinned) {
      initialStateObject.pinnedColumns = {
        right: ['actions'],
      };
    } else if (!pinnedActions && initialPinned) {
      initialStateObject.pinnedColumns = {
        left: initialPinned,
      };
    } else if (pinnedActions && initialPinned) {
      initialStateObject.pinnedColumns = {
        right: ['actions'],
        left: initialPinned,
      };
    }
    if (memoizedColumnVisibilityModel) {
      initialStateObject.columns = {
        columnVisibilityModel: memoizedColumnVisibilityModel,
        all: _.map(combinedColumns, 'field'),
      };
    } else {
      initialStateObject.columns = { all: _.map(combinedColumns, 'field') };
    }
    return initialStateObject;
  }, [
    initialSorting,
    initialFilter,
    pinnedActions,
    initialPinned,
    memoizedColumnVisibilityModel,
    combinedColumns,
    queryParamState,
  ]);

  // propagate new columns to saved datagrid state that predates those columns being added to code
  const newSavedColumnVisibilityModel: { [key: string]: boolean } =
    useMemo(() => {
      const savedColumnVisibilityModel =
        savedDatagridState?.columns?.columnVisibilityModel ||
        memoizedColumnVisibilityModel;
      const newSavedColumnVisibilityModelObject: { [key: string]: boolean } =
        {};
      _.forOwn(memoizedColumnVisibilityModel, (value, key) => {
        newSavedColumnVisibilityModelObject[key] = _.get(
          savedColumnVisibilityModel,
          key,
          value
        );
      });
      return newSavedColumnVisibilityModelObject;
    }, [
      memoizedColumnVisibilityModel,
      savedDatagridState?.columns?.columnVisibilityModel,
    ]);

  const initialGridState = useMemo(() => {
    let baseState;

    if (savedDatagridState) {
      const updatedSavedDatagridState = structuredClone(savedDatagridState);
      _.set(
        updatedSavedDatagridState,
        ['columns', 'columnVisibilityModel'],
        newSavedColumnVisibilityModel
      );
      baseState = updatedSavedDatagridState;
    } else {
      baseState = combinedInitialState;
    }

    // Override with query params if enabled
    if (enableStateQueryParams && queryParamState) {
      baseState = _.merge({}, baseState, queryParamState);
    }

    return baseState;
  }, [
    savedDatagridState,
    combinedInitialState,
    enableStateQueryParams,
    queryParamState,
    newSavedColumnVisibilityModel,
  ]);

  // construct detailPanel attributes based on props
  const detailPanelAttributes: { [key: string]: unknown } = useMemo(() => {
    const getDetailPanelHeight = () => 'auto';
    const getDetailPanelContentCallback = (params: GridRowParams) =>
      getDetailPanelContent && getDetailPanelContent(params);
    const detailPanelAttributesObject: { [key: string]: unknown } = {};
    if (detailPanel) {
      detailPanelAttributesObject.getDetailPanelHeight = getDetailPanelHeight;
      detailPanelAttributesObject.getDetailPanelContent = (
        params: GridRowParams
      ) => getDetailPanelContentCallback(params);
      detailPanelAttributesObject.onDetailPanelExpandedRowIdsChange =
        handleDetailPanelExpandedRowIdsChange;
    }
    if (detailExclusiveOpen) {
      detailPanelAttributesObject.detailPanelExpandedRowIds =
        detailPanelExpandedRowIds;
    }
    return detailPanelAttributesObject;
  }, [
    detailPanel,
    detailExclusiveOpen,
    getDetailPanelContent,
    detailPanelExpandedRowIds.length,
    handleDetailPanelExpandedRowIdsChange,
  ]);

  // inject saved datagrid state for flex and editLocked state into columns
  const columnsWithSavedState = useMemo(() => {
    let columnsWithSavedStateObject = _.chain(combinedColumns)
      .cloneDeep()
      .filter((v) => !!v)
      .value();

    // Apply column ordering from persisted state
    columnsWithSavedStateObject = applyColumnOrdering(
      columnsWithSavedStateObject
    );

    columnsWithSavedStateObject.forEach((c) => {
      if ('flex' in c) {
        c.flex = _.get(
          savedDatagridState,
          ['columns', 'dimensions', c.field, 'flex'],
          c.flex
        );
      }
      if ('editable' in c && !_.get(c, 'allowEditWhileLocked', false)) {
        if (editLocked) {
          c.editable = false;
        }
        if (
          isPermissionDisabled(c?.editPermissionScope ?? editPermissionScope)
        ) {
          c.editable = false;
        }
      }
    });

    // add detail panel toggle column
    if (detailPanel) {
      columnsWithSavedStateObject = [
        {
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          type: 'string',
          hideable: false,
          headerName: 'Expand Toggle',
        },
        ...columnsWithSavedStateObject,
      ];
    }

    // add checkbox selection column
    if (calculatedCheckboxSelection) {
      columnsWithSavedStateObject = [
        {
          ...GRID_CHECKBOX_SELECTION_COL_DEF,
          type: 'string',
          hideable: false,
          headerName: 'Checkbox',
        },
        ...columnsWithSavedStateObject,
      ];
    }

    // inject case insensitive quick filter
    columnsWithSavedStateObject.forEach((c) => {
      if (!('getApplyQuickFilterFn' in c)) {
        c.getApplyQuickFilterFn = getApplyFilterFnCaseInsensitive;
      }
    });

    // add actions column if there are row actions
    if (
      _.filter(combinedRowActions, (action) => !!action.onRowClick).length >
        0 ||
      enableVeltCommenting ||
      getRowClickPath
    ) {
      columnsWithSavedStateObject = [
        ...columnsWithSavedStateObject,
        {
          field: 'actions',
          headerName: 'Actions',
          type: 'actions',
          hideable: false,
          minWidth:
            34 *
              (_.filter(
                combinedRowActions,
                (a) => !!a.onRowClick && !a.showInRowMenu
              ).length +
                (_.some(combinedRowActions, (a) => a.showInRowMenu) ? 1 : 0) +
                (enableVeltCommenting ? 1 : 0) +
                (getRowClickPath ? 1 : 0)) +
            20, // takes into account padding included in column width
          getActions: actionGetter,
        },
      ] as (ExtendedGridColDef | GridActionsColDef)[];
    }

    return columnsWithSavedStateObject;
  }, [
    combinedColumns,
    applyColumnOrdering,
    savedDatagridState?.columns?.dimensions,
    editLocked,
    detailPanel,
    calculatedCheckboxSelection,
    actionGetter,
    combinedRowActions,
    editPermissionScope,
    getRowClickPath,
    enableVeltCommenting,
    isPermissionDisabled,
    savedDatagridState,
  ]);

  // handle state resetting
  const handleResetState = useCallback(() => {
    setPageSize(defaultPageSize);
    const stateToRestore = structuredClone(combinedInitialState);
    _.set(stateToRestore, 'columns.orderedFields', [
      ...(calculatedCheckboxSelection ? ['__check__'] : []),
      ...(detailPanel ? ['__detail_panel_toggle__'] : []),
      ..._.map(combinedColumns, 'field'),
    ]);

    // Default custom property columns to visible
    const customPropertyColumnsVisibilityModel = Object.keys(
      stateToRestore?.columns?.columnVisibilityModel ?? {}
    ).reduce((acc, key) => {
      if (key.includes('customProperties')) {
        acc[key] = true;
      }
      return acc;
    }, {} as { [key: string]: boolean });
    _.update(stateToRestore, 'columns.columnVisibilityModel', (model) => ({
      ...model,
      ...customPropertyColumnsVisibilityModel,
    }));

    // if no initialFilter set, default to no filters
    if (!Object.keys(stateToRestore).includes('filter')) {
      _.set(stateToRestore, 'filter', { filterModel: { items: [] } });
    }

    // if no initialSorting set, default to no sorting
    if (!Object.keys(stateToRestore).includes('sorting')) {
      _.set(stateToRestore, 'sorting', {
        sortModel: [],
      });
    }

    apiRef?.current.restoreState(stateToRestore);

    if (stateToRestore.columns?.orderedFields) {
      apiRef?.current.restoreState({
        columns: { orderedFields: stateToRestore.columns.orderedFields },
      });
    }
  }, [
    defaultPageSize,
    combinedInitialState,
    combinedColumns,
    calculatedCheckboxSelection,
    detailPanel,
  ]);

  const handleSetColumnGroup = useCallback(
    (name: string) => {
      const matchingColumnGroup = _.find(memoizedVisibleGroups || [], {
        name,
      });
      if (matchingColumnGroup) {
        const groupState: { [key: string]: object } = {};
        const groupColumnVisibilityModel: { [key: string]: boolean } = {};

        _.map(combinedColumns, 'field').forEach((col) => {
          groupColumnVisibilityModel[col] =
            matchingColumnGroup.visibleColumns.includes(col);
        });
        groupState.columns = {
          columnVisibilityModel: groupColumnVisibilityModel,
          all: _.map(combinedColumns, 'field'),
          orderedFields: [
            calculatedCheckboxSelection && '__check__',
            detailPanel && '__detail_panel_toggle__',
            ..._.map(combinedColumns, 'field'),
          ],
        };
        apiRef?.current.restoreState(groupState);
      }
    },
    [
      memoizedVisibleGroups,
      combinedColumns,
      calculatedCheckboxSelection,
      detailPanel,
    ]
  );

  // filter columns by app access && allowInBlended
  const memoizedColumns = useMemo(() => {
    const filteredColumns = columnsWithSavedState.filter((c) => {
      const columnAppPermissions = c.permissionScope?.app
        ? appsWithPermissions[c.permissionScope?.app]
        : undefined;
      const allowColumnInBlended = Boolean(
        !!c.allowInBlended &&
          columnAppPermissions?.permissions?.state ===
            appPermissionStates.noBlended
      );
      return !isPermissionDisabled(c?.permissionScope) || allowColumnInBlended;
    });

    const columnsWithEditableParams = filteredColumns.map((column) => {
      if (column.editable && column.inlineEditRenderCell) {
        const getMutationArgs = column.getMutationArgs;
        const mutationPreSubmit = column.mutationPreSubmit;
        const originalRenderCell = column.renderCell;
        const getIsDisabled = column?.mutationGetIsDisabled;
        const getDefaultValue = column?.getMutationDefaultValue;

        column.renderEditCell = (params) =>
          renderMutationCellWithInput({
            params,
            InputElement: column.mutationInputElement ?? undefined,
            validation: column?.mutationValidation,
            permissionScope: column?.editPermissionScope,
            useMutation: column.useMutation,
            getMutationArgs: getMutationArgs
              ? (data: FieldValues) => getMutationArgs(data, params)
              : undefined,
            preSubmit: mutationPreSubmit,
            defaultValue: getDefaultValue ? getDefaultValue(params) : undefined,
            additionalInputElementProps:
              column?.additionalInputElementProps ?? undefined,
            clientV2: column?.mutationAsClientV2 ?? false,
            disableBulkInlineEdit: column?.disableBulkInlineEdit ?? false,
            onSuccess: column?.onEditSuccess,
          });
        column.renderCell = (params) =>
          renderCellWithEditWrapper({
            params,
            renderMethod: originalRenderCell
              ? () => originalRenderCell(params)
              : null,
            permissionScope: column?.editPermissionScope,
            disabled: editLocked,
            getIsDisabled,
          });
      }
      return column;
    });

    if (dynamicFilters) {
      dynamicFilters.forEach((filter) => {
        const columnIndex = _.findIndex(columnsWithEditableParams, [
          'field',
          filter.field,
        ]);

        columnsWithEditableParams[columnIndex] = {
          ...columnsWithEditableParams[columnIndex],
          valueOptions: filter.options,
        };
      });
    }
    return columnsWithEditableParams;
  }, [dynamicFilters, columnsWithSavedState]);

  const [datagridColumnVisibilityModel, setDatagridColumnVisibilityModel] =
    useState(newSavedColumnVisibilityModel);

  // Sync datagridColumnVisibilityModel with newSavedColumnVisibilityModel when it changes.
  // Only backfill missing keys so we don't overwrite user-changed visibility.
  useEffect(() => {
    setDatagridColumnVisibilityModel((prev) => {
      let changed = false;
      const next = { ...prev } as { [key: string]: boolean };
      Object.keys(newSavedColumnVisibilityModel).forEach((key) => {
        if (!(key in prev)) {
          next[key] = newSavedColumnVisibilityModel[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [newSavedColumnVisibilityModel]);

  const exportFields = useCallback(() => {
    // Use memoizedColumns instead of grid state to avoid timing issues when columns change
    const allColumnFields = memoizedColumns.map((col) => col.field);
    const columnVisibilityModel = datagridColumnVisibilityModel;

    const sortedVisibleFields = _.filter(allColumnFields, (field) => {
      // Ensure field is defined and not null/undefined
      if (!field) return false;
      // Filter out internal MUI DataGrid fields and actions column
      if (['actions', '__check__', '__detail_panel_toggle__'].includes(field)) {
        return false;
      }
      return _.get(columnVisibilityModel, field, true);
    });

    const fieldsToExport: string[] = [];
    sortedVisibleFields.forEach((f) => {
      if (
        _.includes(f, 'part') &&
        _.includes(allColumnFields, 'manufacturer') &&
        !_.includes(sortedVisibleFields, 'manufacturer')
      ) {
        fieldsToExport.push(`manufacturer`);
      }
      if (
        _.includes(f, 'part') &&
        _.includes(allColumnFields, 'description') &&
        !_.includes(sortedVisibleFields, 'description')
      ) {
        fieldsToExport.push('description');
      }
      fieldsToExport.push(f);
    });
    return fieldsToExport;
  }, [memoizedColumns, datagridColumnVisibilityModel]);

  const [hasRestoredGridState, setHasRestoredGridState] =
    useState<boolean>(false);
  const [isInitialStateRestoration, setIsInitialStateRestoration] =
    useState<boolean>(true);

  useEffect(() => {
    if (
      apiRefReady &&
      rows.length &&
      !hasRestoredGridState &&
      hasFetchedCustomProperties
    ) {
      let stateToRestore = savedDatagridState;

      // Override with query params if enabled
      if (
        enableStateQueryParams &&
        queryParamState &&
        Object.keys(queryParamState).length > 0
      ) {
        stateToRestore = _.merge({}, savedDatagridState || {}, queryParamState);
      }

      if (stateToRestore) {
        apiRef.current?.restoreState(stateToRestore);

        const allColumnFields = columnsWithSavedState.map((col) => col.field);
        const savedOrder = stateToRestore?.columns?.orderedFields || [];

        const orderedFields = [
          ...savedOrder.filter((field) => allColumnFields.includes(field)),
          ...allColumnFields.filter((field) => !savedOrder.includes(field)),
        ];

        // allow MUI grid state to settle before applying column order
        setTimeout(() => {
          apiRef.current?.restoreState({
            columns: {
              orderedFields,
            },
          });
          // Force recomputation of cached grid state
          apiRef.current?.publishEvent('pageSizeChange', pageSize);
          setHasRestoredGridState(true);
          // Mark initial state restoration as complete after a short delay
          // to allow all initial state change events to fire
          setTimeout(() => {
            setIsInitialStateRestoration(false);
          }, 100);
        }, 0);
      } else {
        setHasRestoredGridState(true);
        // Mark initial state restoration as complete after a short delay
        setTimeout(() => {
          setIsInitialStateRestoration(false);
        }, 100);
      }
    }
  }, [
    columnsWithSavedState,
    apiRef,
    rows,
    apiRefReady,
    savedDatagridState,
    hasRestoredGridState,
    setHasRestoredGridState,
    hasFetchedCustomProperties,
    pageSize,
    enableStateQueryParams,
    queryParamState,
  ]);

  const gridLoading = loading || isFetchingCustomProperties;

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        height: fullCard ? '100%' : undefined,
      }}
    >
      {gridLoading && verboseLoader && <VerboseLoader {...verboseLoader} />}
      {showCharts && (
        <DataGridChartsContainer
          apiRef={apiRef}
          datagridColumnVisibilityModel={datagridColumnVisibilityModel}
          loading={gridLoading}
          loadingCharts={loadingCharts}
          memoizedColumns={memoizedColumns}
          rows={rows}
        />
      )}
      <DataGridPremium
        {...others}
        localeText={{
          toolbarColumns: narrowWindow ? '' : 'Columns',
          toolbarFilters: narrowWindow ? '' : 'Filters',
          toolbarExport: narrowWindow ? '' : 'Export',
        }}
        disableRowGrouping
        experimentalFeatures={{
          ...(editable ? { newEditingApi: true } : {}),
          ...(aggregationModel ? { aggregation: true } : {}),
        }}
        columns={memoizedColumns}
        aggregationModel={aggregationModel}
        rows={rows}
        throttleRowsMs={100}
        getRowHeight={noRowHeightAuto ? undefined : () => 'auto'}
        getRowId={getRowId || ((row) => row.id)}
        autoHeight={!fullCard}
        loading={(gridLoading && !verboseLoader) || isLoadingAppAccess}
        pagination={pagination}
        apiRef={apiRef}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChangeCallback}
        rowsPerPageOptions={pageSizeOptions}
        page={page}
        onPageChange={(newPage) => {
          onPageChange(newPage);
          setPage(newPage);
          if (enableStateQueryParams) {
            updateQueryParams({
              page: newPage > 0 ? newPage.toString() : null,
            });
          }
        }}
        onRowClick={
          onRowClick ||
          (getRowClickPath
            ? (params: GridRowParams<T>) => {
                if (getRowClickOrg && blended) {
                  changeActiveOrg(getRowClickOrg(params.row));
                }
                navigate(getRowClickPath(params.row));
              }
            : undefined)
        }
        onCellEditStop={(params: GridCellEditStopParams, event: MuiEvent) => {
          if (params.reason === GridCellEditStopReasons.cellFocusOut) {
            // Allow Enter to be handled by react-hook-form
            event.defaultMuiPrevented = true;
          }
        }}
        sx={{
          ...sx,
          ...initialSx,
        }}
        disableSelectionOnClick
        checkboxSelection={calculatedCheckboxSelection}
        disableColumnReorder={false}
        components={{
          DetailPanelExpandIcon: IconPlus,
          DetailPanelCollapseIcon: IconMinus,
          ExportIcon: IconDownload,
          OpenFilterButtonIcon: IconFilter,
          ColumnSelectorIcon: IconColumns,
          LoadingOverlay: LinearProgress,
          NoRowsOverlay: NoRowsOverlayComponent,
          Toolbar: ExtendedGridToolbar<T, ReportDataType>,
        }}
        componentsProps={{
          noRowsOverlay: { noRows },
          toolbar: {
            apiRef,
            exportCsv,
            fullCard,
            columns: combinedColumns,
            handleResetState,
            handleSetColumnGroup,
            hideToolbar,
            isExportLoading,
            memoizedVisibleGroups,
            toolbarColumns,
            toolbarExport,
            toolbarFilters,
            children,
            exportFields,
            exportFileNamePrefix,
            gridName,
            generateReportData,
            newRecordButton,
            uploadRecordButton,
            rowActions: combinedRowActions,
            selectedRowData,
            selectedRowIds,
            toolbarQuickFilter,
            toolbarReset,
            shouldSaveDatagridState,
            initialGridState,
            enableStateQueryParams,
            updateQueryParams,
          },
        }}
        // upon subsequent state changes, the initial state may not be applied anymore.
        initialState={initialGridState}
        columnVisibilityModel={datagridColumnVisibilityModel}
        onColumnVisibilityModelChange={(newModel, details) => {
          const processedModel = _.isEmpty(newModel)
            ? memoizedColumns.reduce((acc: { [key: string]: boolean }, col) => {
                if (
                  !['actions', '__check__', '__detail_panel_toggle__'].includes(
                    col.field
                  )
                ) {
                  acc[col.field] = true;
                }
                return acc;
              }, {})
            : newModel;
          setDatagridColumnVisibilityModel(processedModel);
          others?.onColumnVisibilityModelChange?.(processedModel, details);
        }}
        onSelectionModelChange={(
          selectionModel: GridSelectionModel,
          callback: GridCallbackDetails
        ) => {
          onSelectionModelChange?.(selectionModel, callback);
          handleSelect(selectionModel);
        }}
        onFilterModelChange={(model, details) => {
          if (enableStateQueryParams && !isInitialStateRestoration) {
            const filterItems = model.items.filter(
              (item) => item.value !== undefined && item.value !== ''
            );
            updateQueryParams({
              filter:
                filterItems.length > 0 ? JSON.stringify(filterItems) : null,
              quickFilter: model.quickFilterValues?.length
                ? model.quickFilterValues.join(',')
                : null,
            });
          }
          others.onFilterModelChange?.(model, details);
        }}
        onSortModelChange={(model, details) => {
          if (enableStateQueryParams && !isInitialStateRestoration) {
            updateQueryParams({
              sort: model.length > 0 ? JSON.stringify(model) : null,
            });
          }
          others.onSortModelChange?.(model, details);
        }}
        {...detailPanelAttributes}
      />
      {recordTypeDefinition && recordTypeDefinition?.deleteMutation && (
        <>
          <DeleteRecordDialog<T>
            id={selectedRow?.id ?? ''}
            dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.DELETE)}
            onClose={closeDialog}
            recordName={recordTypeDefinition?.singularName}
            useClientDeleteMutation={recordTypeDefinition.deleteMutation}
          />
          <DeleteRecordsDialog<T>
            ids={selectedRowIds as string[]}
            dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.BATCH_DELETE)}
            onClose={closeDialog}
            recordName={recordTypeDefinition?.singularName}
            useClientDeleteMutation={recordTypeDefinition.deleteMutation}
          />
        </>
      )}
      {recordTypeDefinition?.approveMutation &&
        recordTypeDefinition?.unapproveMutation && (
          <>
            <ToggleRecordDialog<
              T,
              typeof recordTypeDefinition.approveMutation,
              typeof recordTypeDefinition.unapproveMutation
            >
              id={selectedRow?.id ?? ''}
              isCurrentlyTrue={!!selectedRow?.approvedAt}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.APPROVE)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.approveMutation}
              useToggleFalseMutation={recordTypeDefinition?.unapproveMutation}
              toggleActionType={ToggleRecordActionTypes.APPROVE}
            />
            <ToggleRecordsDialog<T>
              ids={selectedRowIds as string[]}
              isCurrentlyTrue={selectedRowData.every((row) => !!row.approvedAt)}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.BATCH_APPROVE)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.approveMutation}
              useToggleFalseMutation={recordTypeDefinition?.unapproveMutation}
              toggleActionType={ToggleRecordActionTypes.APPROVE}
            />
          </>
        )}
      {recordTypeDefinition?.lockMutation &&
        recordTypeDefinition?.unlockMutation && (
          <>
            <ToggleRecordDialog<
              T,
              typeof recordTypeDefinition.lockMutation,
              typeof recordTypeDefinition.unlockMutation
            >
              id={selectedRow?.id ?? ''}
              isCurrentlyTrue={!!selectedRow?.lockedAt}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.LOCK)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.lockMutation}
              useToggleFalseMutation={recordTypeDefinition?.unlockMutation}
              toggleActionType={ToggleRecordActionTypes.LOCK}
            />
            <ToggleRecordsDialog<T>
              ids={selectedRowIds as string[]}
              isCurrentlyTrue={selectedRowData.every((row) => !!row.lockedAt)}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.BATCH_LOCK)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.lockMutation}
              useToggleFalseMutation={recordTypeDefinition?.unlockMutation}
              toggleActionType={ToggleRecordActionTypes.LOCK}
            />
          </>
        )}
      {recordTypeDefinition?.voidMutation &&
        recordTypeDefinition?.unvoidMutation && (
          <>
            <ToggleRecordDialog<
              T,
              typeof recordTypeDefinition.voidMutation,
              typeof recordTypeDefinition.unvoidMutation
            >
              id={selectedRow?.id ?? ''}
              isCurrentlyTrue={!!selectedRow?.voidedAt}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.VOID)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.voidMutation}
              useToggleFalseMutation={recordTypeDefinition?.unvoidMutation}
              toggleActionType={ToggleRecordActionTypes.VOID}
            />
            <ToggleRecordsDialog<T>
              ids={selectedRowIds as string[]}
              isCurrentlyTrue={selectedRowData.every((row) => !!row.voidedAt)}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.BATCH_VOID)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.voidMutation}
              useToggleFalseMutation={recordTypeDefinition?.unvoidMutation}
              toggleActionType={ToggleRecordActionTypes.VOID}
            />
          </>
        )}
      {recordTypeDefinition?.archiveMutation &&
        recordTypeDefinition?.unarchiveMutation && (
          <>
            <ToggleRecordDialog<
              T,
              typeof recordTypeDefinition.archiveMutation,
              typeof recordTypeDefinition.unarchiveMutation
            >
              id={selectedRow?.id ?? ''}
              isCurrentlyTrue={!!selectedRow?.archivedAt}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.ARCHIVE)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.archiveMutation}
              useToggleFalseMutation={recordTypeDefinition?.unarchiveMutation}
              toggleActionType={ToggleRecordActionTypes.ARCHIVE}
            />
            <ToggleRecordsDialog<T>
              ids={selectedRowIds as string[]}
              isCurrentlyTrue={selectedRowData.every((row) => !!row.archivedAt)}
              dialogOpen={isDialogOpen(RECORD_ACTION_DIALOGS.BATCH_ARCHIVE)}
              onClose={closeDialog}
              recordName={recordTypeDefinition?.singularName}
              useToggleTrueMutation={recordTypeDefinition?.archiveMutation}
              useToggleFalseMutation={recordTypeDefinition?.unarchiveMutation}
              toggleActionType={ToggleRecordActionTypes.ARCHIVE}
            />
          </>
        )}
    </Box>
  );
};

export default ExtendedDatagrid;
